/**
 * @override
 * Overrides usePageBySlug to resolve page by current region: fetches pages with the same slug
 * (limit 20), then picks the one whose Regions field includes the current region or "Global".
 * Supports KL-925: same slug can have different content per region (AU, NZ, SG, MY, CA, Global).
 * Also overrides usePageById with an 8KB-safe slim by-id query. usePages is re-exported from
 * base so direct imports from hooks/use-page still resolve it.
 */

import {useMemo} from 'react';
import {print} from 'graphql';
import {QueryClient} from '@tanstack/react-query';

export {usePages} from '@amblique/contentful/app/contentful/hooks/use-page';

import {useQuery, useQueryClient} from '@tanstack/react-query';
import {getCategory as getCategoryQueryKeyHelper, getProduct as getProductQueryKeyHelper} from '@salesforce/commerce-sdk-react/hooks/ShopperProducts/queryKeyHelpers';
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {
  getFullPageBySlugForRegion,
  getPageById,
  getPageBySlugForRegion,
} from '@amblique/king-living/app/contentful/queries/page.queries';
import {getRegionFromLocale, selectPageByRegion} from '@amblique/king-living/app/contentful/helpers/region';
import {getBaseBlogById} from '@amblique/contentful/app/contentful/queries/blog.queries';
import {getAccordionById} from '@amblique/contentful/app/contentful/queries/accordion.queries';
import {getAccordionPanelById} from '@amblique/contentful/app/contentful/queries/accordion-panel.queries';
import {getContainerById} from '@amblique/contentful/app/contentful/queries/container.queries';
import {getSectionPanelById} from '@amblique/contentful/app/contentful/queries/section-panel.queries';
import {
  buildSlugQueryVariants,
  findBlogIdInMainRegion,
  isBlogArticlePage,
  mapAccordionsToFaqQuestions,
  PRODUCT_SEO_EXPAND,
  resolveRobotsContent,
} from '@amblique/king-living/app/utils/seo-utils';
import {getSlugFromPathname} from '@amblique/king-living/app/utils/slug-from-path';

const SYSTEM_PATH_PATTERN = /^\/(\.well-known|__mrt|favicon\.ico)(\/|$)/;
const PREFETCH_TIMEOUT_MS = 8000;
const PAGE_STALE_TIME_MS = 15 * 60 * 1000;
const PAGE_GC_TIME_MS = 30 * 60 * 1000;
const isServerSide = typeof window === 'undefined';

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((resolve) => {
      setTimeout(() => resolve(undefined), ms);
    })
  ]);
}

async function prefetchWithTimeoutRetry(fetchFn) {
  let result = await withTimeout(fetchFn(), PREFETCH_TIMEOUT_MS);

  if (!result) {
    result = await withTimeout(fetchFn(), PREFETCH_TIMEOUT_MS);
  }

  return result;
}

function shouldRetryContentfulQuery(failureCount, error) {
  if (error?.status >= 400 && error?.status < 500) {
    return false;
  }

  return failureCount < 3;
}

function getCommerceOrganizationId() {
  return getConfig().app?.commerceAPI?.parameters?.organizationId;
}

export function buildPageBySlugQueryKey({contentfulService, slug, site, variant = 'default', variables = {}}) {
  return [
    contentfulService?.key,
    'page',
    'slug',
    buildSlugQueryVariants(slug).join('|'),
    variant,
    contentfulService?.locale,
    site?.id,
    getRegionFromLocale(contentfulService?.locale, site?.id),
    JSON.stringify(variables)
  ];
}

function resolveShopperLocaleId(locale) {
  return String(locale?.id ?? locale).replace(/_/g, '-');
}

export function buildProductSeoQueryKey({site, locale, productId}) {
  return getProductQueryKeyHelper.queryKey({
    allImages: true,
    expand: PRODUCT_SEO_EXPAND,
    id: productId,
    locale: resolveShopperLocaleId(locale),
    organizationId: getCommerceOrganizationId(),
    perPricebook: true,
    siteId: site.id,
  });
}

export function buildCategorySeoQueryKey({site, locale, categoryId}) {
  return getCategoryQueryKeyHelper.queryKey({
    id: categoryId,
    locale: resolveShopperLocaleId(locale),
    organizationId: getCommerceOrganizationId(),
    siteId: site.id,
  });
}

async function fetchShopperApiJson({site, requestPath}) {
  const {app} = getConfig();
  const {shortCode, organizationId} = app?.commerceAPI?.parameters ?? {};

  if (!shortCode || !organizationId) {
    return null;
  }

  const url =
    `https://${shortCode}.api.commercecloud.salesforce.com` +
    `/product/shopper-products/v1/organizations/${organizationId}` +
    requestPath;

  const {slasTokenService} = await import('../../../../../app/server/auth/slas-token-service.js');
  const token = await slasTokenService.getGuestTokenForSite(site.id);
  const res = await fetch(url, {headers: {Authorization: `Bearer ${token}`}});

  return res.ok ? res.json() : null;
}

async function fetchShopperProductForSeoServer({site, locale, productId}) {
  if (!productId) {
    return null;
  }

  const localeId = resolveShopperLocaleId(locale);
  const qs = new URLSearchParams({
    allImages: 'true',
    expand: PRODUCT_SEO_EXPAND.join(','),
    locale: localeId,
    perPricebook: 'true',
    siteId: site.id,
  });
  
  qs.set('expand', PRODUCT_SEO_EXPAND.join(','));

  return fetchShopperApiJson({
    site,
    requestPath: `/products/${encodeURIComponent(productId)}?${qs.toString()}`,
  });
}

async function fetchShopperCategoryForSeoServer({site, locale, categoryId}) {
  if (!categoryId) {
    return null;
  }

  const qs = new URLSearchParams({
    locale: resolveShopperLocaleId(locale),
    siteId: site.id,
  });

  return fetchShopperApiJson({
    site,
    requestPath: `/categories/${encodeURIComponent(categoryId)}?${qs.toString()}`,
  });
}

function shouldPrefetchCategoryFallback(slug) {
  return Boolean(slug) && !slug.startsWith('page/') && !slug.startsWith('product/');
}

function cachePrefetchedCategory(queryClient, categoryQueryKey, category) {
  if (category && categoryQueryKey) {
    queryClient.setQueryData(categoryQueryKey, category);
  }
}

async function prefetchSeoCategory({queryClient, categoryQueryKey, categoryId, locale, site}) {
  if (!categoryQueryKey || queryClient.getQueryData(categoryQueryKey) !== undefined) {
    return;
  }

  try {
    const category = await prefetchWithTimeoutRetry(() => fetchShopperCategoryForSeoServer({categoryId, locale, site}));

    cachePrefetchedCategory(queryClient, categoryQueryKey, category);
  } catch (error) {
    console.error('[prefetchDynamicPageSeoQueries] category prefetch failed:', error);
  }
}

async function prefetchSeoProduct({queryClient, productQueryKey, locale, productId, res, site}) {
  if (queryClient.getQueryData(productQueryKey) !== undefined) {
    return;
  }

  try {
    const product = await prefetchWithTimeoutRetry(() => fetchShopperProductForSeoServer({locale, productId, site}));

    if (product) {
      queryClient.setQueryData(productQueryKey, product);
      res.locals.__seoProductPrefetch = product;
    }
  } catch (error) {
    console.error('[prefetchDynamicPageSeoQueries] product prefetch failed:', error);
  }
}

async function fetchContentfulEntry(contentfulService, query, id) {
  const data = await contentfulService.query(print(query), {
    id,
    locale: contentfulService.locale,
    preview: false,
  });

  return data;
}

const FAQ_NESTED_REGION_FETCH = {
  Accordion: {
    isLeaf: true,
    parentKey: 'accordion',
    query: getAccordionById,
  },
  Container: {
    parentKey: 'container',
    query: getContainerById,
    getItems: (parent) => parent?.region1Collection?.items || parent?.region1CursorCollection?.items || [],
  },
  SectionPanel: {
    parentKey: 'sectionPanel',
    query: getSectionPanelById,
    getItems: (parent) => parent?.contentCollection?.items || parent?.contentCursorCollection?.items || [],
  },
};

async function fetchFaqQuestionsFromAccordionId(contentfulService, id) {
  const data = await prefetchWithTimeoutRetry(() => fetchContentfulEntry(contentfulService, getAccordionPanelById, id));
  const panel = data?.accordionPanel;

  if (!panel?.isFaq) return [];

  return mapAccordionsToFaqQuestions(panel.accordionsCollection?.items);
}

async function collectFaqQuestionsFromRegionItems(contentfulService, items) {
  const questions = [];

  for (const item of items || []) {
    const id = item?.sys?.id;
    const typename = item?.__typename;

    if (!id) continue;

    if (typename === 'AccordionPanel') {
      questions.push(...await fetchFaqQuestionsFromAccordionId(contentfulService, id));
      continue;
    }

    const nestedFetch = FAQ_NESTED_REGION_FETCH[typename];

    if (!nestedFetch) continue;

    const data = await prefetchWithTimeoutRetry(() => fetchContentfulEntry(contentfulService, nestedFetch.query, id));
    const parent = data?.[nestedFetch.parentKey];

    if (nestedFetch.isLeaf) {
      questions.push(...mapAccordionsToFaqQuestions([parent]));
      continue;
    }

    questions.push(
      ...await collectFaqQuestionsFromRegionItems(contentfulService, nestedFetch.getItems(parent))
    );
  }

  return questions;
}

async function prefetchFaqQuestions({contentfulService, page, res}) {
  if (!page?.mainRegionCollection?.items?.length) return;

  try {
    const faqQuestions = await collectFaqQuestionsFromRegionItems(
      contentfulService,
      page.mainRegionCollection?.items
    );

    if (!faqQuestions.length) return;

    res.locals.__seoFaqQuestions = faqQuestions;
  } catch (error) {
    console.error('[prefetchDynamicPageSeoQueries] FAQ prefetch failed:', error);
  }
}

async function prefetchBlogForSeo({contentfulService, page, res}) {
  if (!isBlogArticlePage(page)) return;

  const blogId = findBlogIdInMainRegion(page);

  if (!blogId) return;

  try {
    const data = await prefetchWithTimeoutRetry(() => fetchContentfulEntry(contentfulService, getBaseBlogById, blogId));
    const blog = data?.blog;

    if (blog) {
      res.locals.__seoBlogPrefetch = blog;
    }
  } catch (error) {
    console.error('[prefetchDynamicPageSeoQueries] Blog prefetch failed:', error);
  }
}

/**
 * @param {{ res?: object, location?: { pathname?: string } }} args
 */
export async function prefetchDynamicPageSeoQueries({res, location} = {}) {
  if (!isServerSide || !res?.locals) {
    return {};
  }

  const pathname = location?.pathname;

  if (!pathname || SYSTEM_PATH_PATTERN.test(pathname)) {
    return {};
  }

  if (resolveRobotsContent({pathname}) !== 'INDEX,FOLLOW') {
    return {};
  }

  const {site, locale} = res.locals;

  if (!site?.preferences?.contentful?.spaceId || !site?.preferences?.contentful?.deliveryAccessToken) {
    return {};
  }

  let contentfulService;

  try {
    contentfulService = ContentfulService.instantiate({preview: false, site, locale});
  } catch {
    return {};
  }

  const slugPrimary = getSlugFromPathname(pathname) || 'home';

  if (!buildSlugQueryVariants(slugPrimary).length) {
    return {};
  }

  const queryClient = res.locals.__queryClient ?? new QueryClient({
    defaultOptions: {
      queries: {retry: false, refetchOnWindowFocus: false, staleTime: 10 * 1000, retryOnMount: false},
      mutations: {retry: false},
    },
  });

  res.locals.__queryClient = queryClient;

  const pageQueryKey = buildPageBySlugQueryKey({contentfulService, site, slug: slugPrimary});
  let page = queryClient.getQueryData(pageQueryKey);
  const categoryIdFromPath = slugPrimary.replace(/\//g, '-');
  const shouldPrefetchCategory = shouldPrefetchCategoryFallback(slugPrimary);
  const categoryQueryKey = shouldPrefetchCategory ?
    buildCategorySeoQueryKey({
      categoryId: categoryIdFromPath,
      locale,
      site,
    }) :
    null;
  const shouldFetchCategory =
    shouldPrefetchCategory &&
    categoryQueryKey &&
    queryClient.getQueryData(categoryQueryKey) === undefined;

  if (page === undefined) {
    try {
      const fetchedPage = await withTimeout(
        (async () => {
          const slugVariants = buildSlugQueryVariants(slugPrimary);

          if (!slugVariants.length || !contentfulService) {
            return null;
          }

          const data = await contentfulService.query(
            print(getPageBySlugForRegion),
            {
              locale: contentfulService.locale,
              preview: false,
              slugs: slugVariants,
            }
          );

          return selectPageByRegion(
            data?.pageCollection?.items ?? [],
            getRegionFromLocale(contentfulService.locale, site?.id)
          );
        })(),
        PREFETCH_TIMEOUT_MS
      );

      // Only cache a definitive Contentful result. Timeout (undefined) must not write null.
      if (fetchedPage !== undefined) {
        page = fetchedPage ?? null;
        queryClient.setQueryData(pageQueryKey, page);
      }
    } catch (error) {
      console.error('[prefetchDynamicPageSeoQueries] page prefetch failed:', error);
    }
  }

  if (shouldFetchCategory && !page) {
    await prefetchSeoCategory({
      categoryId: categoryIdFromPath,
      categoryQueryKey,
      locale,
      queryClient,
      site,
    });
  }

  const resolvedPage = queryClient.getQueryData(pageQueryKey);

  if (resolvedPage?.productId) {
    await prefetchSeoProduct({
      locale,
      productId: resolvedPage.productId,
      productQueryKey: buildProductSeoQueryKey({
        locale,
        productId: resolvedPage.productId,
        site,
      }),
      queryClient,
      res,
      site,
    });
  } else if (resolvedPage?.categoryId) {
    await prefetchSeoCategory({
      categoryId: resolvedPage.categoryId,
      categoryQueryKey: buildCategorySeoQueryKey({
        categoryId: resolvedPage.categoryId,
        locale,
        site,
      }),
      locale,
      queryClient,
      site,
    });
  }

  if (resolvedPage) {
    await prefetchFaqQuestions({
      contentfulService,
      page: resolvedPage,
      res,
    });
    await prefetchBlogForSeo({
      contentfulService,
      page: resolvedPage,
      res,
    });
  }

  return {};
}

/**
 * Fetches a Page by slug and resolves the correct variant for the current region.
 *
 * @override usePageBySlug with region-aware resolution.
 */
export function usePageBySlug(slug, options = {}) {
  const {site} = useMultiSite();
  const contentfulService = useService(ContentfulService);
  const queryClient = useQueryClient();

  const {
    variant = 'default',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  const slugVariantsKey = useMemo(() => buildSlugQueryVariants(slug).join('|'), [slug]);
  const variablesKey = JSON.stringify(variables);
  const {enabled: queryEnabled = true, ...restQueryOptions} = queryOptions;

  const query = variant === 'full' ? getFullPageBySlugForRegion : getPageBySlugForRegion;
  const currentRegion = getRegionFromLocale(contentfulService?.locale, site?.id);

  const queryKey = useMemo(() => {
    if (!contentfulService || !slugVariantsKey) {
      return null;
    }

    return buildPageBySlugQueryKey({contentfulService, site, slug, variant, variables});
  }, [contentfulService, site, slug, slugVariantsKey, variant, variablesKey]);

  return useQuery({
    queryKey: queryKey ?? ['contentful', 'page', 'slug', 'disabled'],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const slugVariants = buildSlugQueryVariants(slug);

      if (!slugVariants.length) {
        return null;
      }

      const data = await contentfulService.query(
        print(query),
        {
          locale: contentfulService.locale,
          preview: false,
          slugs: slugVariants,
          ...variables,
        },
        requestOptions
      );

      return selectPageByRegion(data?.pageCollection?.items ?? [], currentRegion);
    },

    enabled: !!queryKey && queryEnabled,

    // SSR: read prefetched page so dependent hooks (e.g. useCategory in DynamicPage) enable during prepass.
    initialData: () => (queryKey ? queryClient.getQueryData(queryKey) : undefined),

    staleTime: PAGE_STALE_TIME_MS,
    gcTime: PAGE_GC_TIME_MS,
    refetchOnWindowFocus: false,

    retry: shouldRetryContentfulQuery,

    ...restQueryOptions,
  });
}

/**
 * Fetches a Page by its Contentful entry id using the SLIM query.
 *
 * @override of the base `usePageById`. The base hook uses the full `PageFragment`,
 * which exceeds Contentful's ~8KB query limit and fails. This override mirrors the
 * slug path (`usePageBySlug`) by requesting the slim fragment (block ids only); each
 * region block then lazy-loads its full data via Component `useXById` hooks. Used by
 * the preview page to render slug-less Pages via `/{site}/preview/<entryId>`.
 *
 * @param {string} id - Contentful entry id
 * @param {Object} [options={}]
 * @param {Omit<Contentful.QueryPageArgs, 'id'>} [options.variables={}]
 * @param {import('@tanstack/react-query').UseQueryOptions} [options.queryOptions={}]
 * @param {RequestInit} [options.requestOptions={}]
 * @returns {import('@tanstack/react-query').UseQueryResult<Contentful.Page|null, Error>}
 */
export function usePageById(id, options = {}) {
  const contentfulService = useService(ContentfulService);

  const {variables = {}, queryOptions = {}, requestOptions = {}} = options;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'page',
      'id',
      'slim',
      id,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        print(getPageById),
        {
          id,
          locale: contentfulService.locale,
          preview: false,
          ...variables,
        },
        requestOptions
      );

      return data.page || null;
    },

    enabled: !!contentfulService && !!id,

    staleTime: PAGE_STALE_TIME_MS,
    gcTime: PAGE_GC_TIME_MS,
    refetchOnWindowFocus: false,

    retry: shouldRetryContentfulQuery,

    ...queryOptions,
  });
}
